import { createContext, useCallback, useContext, useMemo, type ReactNode } from 'react';
import { useToast } from '@chakra-ui/react';
import { skipToken } from '@tanstack/react-query';
import { t } from '@lingui/core/macro';

import { trpcAdmin } from '@duke-hq/app-admin-web/lib/trpcAdmin';
import { StudentFileResourceKind } from '@duke-hq/svc-api-db';

import { markingStatus } from '../utils';
import type { AssessmentSubmission } from '../types';
import { useAssessorSlides } from './useAssessorSlides';
import { useOverviewSubmissionState } from './useOverviewSubmissionState';

function useMarkingState({
  assessmentId,
  assessmentSubmission,
}: {
  assessmentId: string;
  assessmentSubmission: AssessmentSubmission;
}) {
  const toast = useToast();
  const utils = trpcAdmin.useUtils();
  const isAlreadyAssessed = assessmentSubmission.assessed_at !== null;

  const navigation = useAssessorSlides({
    assessorSlides: assessmentSubmission.assessorSlides,
    isAlreadyAssessed,
  });

  const { currentSlide, onNextUnmarked } = navigation;

  const currentAnswer = currentSlide?.answer;

  const { data: currentAnswerFeedback } = trpcAdmin.assessments.getAnswerFeedbackById.useQuery(
    currentAnswer?.unitAssessmentEnrolmentId != null
      ? {
          answerId: currentAnswer.originalAnswerId,
          unitAssessmentEnrolmentId: currentAnswer.unitAssessmentEnrolmentId,
        }
      : skipToken,
  );
  const currentAnswerHasFeedback = (currentAnswerFeedback?.feedback ?? '').trim() !== '';

  const overviewState = useOverviewSubmissionState({
    assessmentSubmission,
    enabled: navigation.isOverviewScreen,
  });

  const { mutateAsync: getSignedDownloadUrl } =
    trpcAdmin.assessments.getSubmissionFileDownloadUrl.useMutation();

  const getSubmissionFileUrl = useCallback(
    async (fileId: string) => {
      const { signedUrl } = await getSignedDownloadUrl({
        fileId,
        resourceId: assessmentSubmission.unitAssessmentEnrolment?.id,
        resourceKind: StudentFileResourceKind.ASSESSMENT_SLIDE_ANSWER,
      });
      return signedUrl;
    },
    [assessmentSubmission.unitAssessmentEnrolment?.id, getSignedDownloadUrl],
  );

  const handleDownloadFile = useCallback(
    async (fileId: string) => {
      try {
        window.open(await getSubmissionFileUrl(fileId), '_blank');
      } catch (error) {
        toast({
          title: t`Error`,
          description:
            (error instanceof Error && error.message) ||
            t`There was an error downloading your file. Please try again. If the issue persists, please contact support.`,
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      }
    },
    [toast, getSubmissionFileUrl],
  );

  const {
    mutate: updateStudentAnswer,
    isPending: isMarkingPending,
    variables: markingVariables,
  } = trpcAdmin.assessments.updateStudentAnswer.useMutation({
    onSuccess: async (_data, variables) => {
      if (currentAnswer?.unitAssessmentEnrolmentId != null) {
        await utils.assessments.getAnswerFeedbackById.invalidate({
          answerId: currentAnswer.originalAnswerId,
          unitAssessmentEnrolmentId: currentAnswer.unitAssessmentEnrolmentId,
        });
      }

      await utils.assessments.getById.refetch(Number(assessmentId));

      if (currentAnswer?.unitAssessmentEnrolmentId != null) {
        await utils.assessments.getAllByAssessmentEnrolmentId.invalidate(
          currentAnswer.unitAssessmentEnrolmentId,
        );
      }

      if (variables.correct === true) {
        await utils.assessments.getCalculatedOutcome.invalidate();
        onNextUnmarked();
      }
    },
    onError: (error) => {
      toast({
        title: t`Error`,
        description:
          (error instanceof Error && error.message) ||
          t`There was an error saving the outcome. Please try again. If the issue persists, please contact support.`,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    },
  });

  const handleStatusChange = useCallback(
    (correct: boolean) => {
      if (
        isMarkingPending ||
        currentAnswer == null ||
        currentAnswer.unitAssessmentEnrolmentId == null ||
        assessmentSubmission.id == null
      ) {
        return;
      }

      updateStudentAnswer({
        id: currentAnswer.originalAnswerId,
        unitAssessmentEnrolmentId: currentAnswer.unitAssessmentEnrolmentId,
        assessmentSubmissionId: assessmentSubmission.id,
        correct,
      });
    },
    [assessmentSubmission.id, currentAnswer, isMarkingPending, updateStudentAnswer],
  );

  const onMarkCorrect = useCallback(() => handleStatusChange(true), [handleStatusChange]);
  const onMarkRevision = useCallback(() => handleStatusChange(false), [handleStatusChange]);
  const markingLoadingType = markingStatus(markingVariables?.correct);

  return useMemo(
    () => ({
      assessmentSubmission,
      currentAnswer,
      currentAnswerFeedback,
      currentAnswerHasFeedback,
      overviewState,
      getSubmissionFileUrl,
      handleDownloadFile,
      onMarkCorrect,
      onMarkRevision,
      isMarkingPending,
      markingLoadingType,
      ...navigation,
    }),
    [
      assessmentSubmission,
      currentAnswer,
      currentAnswerFeedback,
      currentAnswerHasFeedback,
      overviewState,
      getSubmissionFileUrl,
      handleDownloadFile,
      onMarkCorrect,
      onMarkRevision,
      isMarkingPending,
      markingLoadingType,
      navigation,
    ],
  );
}

export const MarkingContext = createContext<ReturnType<typeof useMarkingState> | null>(null);

export function MarkingProvider({
  assessmentId,
  assessmentSubmission,
  children,
}: {
  assessmentId: string;
  assessmentSubmission: AssessmentSubmission;
  children: ReactNode;
}) {
  const value = useMarkingState({ assessmentId, assessmentSubmission });
  return <MarkingContext.Provider value={value}>{children}</MarkingContext.Provider>;
}

export function useMarkingContext() {
  const context = useContext(MarkingContext);

  if (context === null) {
    throw new Error('useMarkingContext must be used within a MarkingProvider');
  }

  return context;
}
